import React, { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../../config/URL";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import fetchAllStudentsWithIds from "../../List/StudentList";
import CustomDatePicker from "../../CustomDatePicker";
import Select from "react-select";
import fetchAllCoursesWithIdsC from "../../List/CourseListByCenter";
import fetchAllClassesWithIdsC from "../../List/ClassListByCourse";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("*Name is required"),
  assessmentDate: Yup.date().required("*Assessment Date is required"),
  year: Yup.date()
    .notRequired("*Date of Birth is required")
    .max(
      new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
      "*Date of Birth must be at least 1 year ago"
    ),
  courseId: Yup.string().required("*Course is required"),
  classId: Yup.string().required("*Class is required"),
  remarks: Yup.string()
    .notRequired()
    .max(200, "*The maximum length is 200 characters"),
});

const AssessmentChild = forwardRef(
  ({ formData, setLoadIndicators, setFormData, handleNext }, ref) => {
    const { leadId } = useParams();
    const [studentData, setStudentData] = useState(null);
    const [assessmentAvailable, setAssessmentAvailable] = useState(false);
    const [assessmentId, setAssessmentId] = useState(false);
    const [arrangeassesmentData, setArrangeassesmentData] = useState([]);
    const currentDate = new Date().toISOString().split("T")[0];
    const [centerId, setCenterId] = useState(null);
    const [courseOptions, setCourseOptions] = useState([]);
    const [classOptions, setClassOptions] = useState([]);

    const formik = useFormik({
      initialValues: {
        name: formData.name || "",
        centerId: formData.centerId || "",
        classId: formData.classId || "",
        courseId: formData.courseId || "",
        assessmentDate: formData.assessmentDate || currentDate,
        age: formData.age || "",
        year: formData.year || "",
        pictureToken: formData.pictureToken || "",
        paymentMode: formData.paymentMode || "",
        timeSlotOffered: formData.timeSlotOffered || "",
        referredBy: formData.referredBy || "",
        tshirtSize: formData.tshirtSize || "",
        levelAssessed: formData.levelAssessed || "",
        sibling: formData.sibling || "",
        whereFrom: formData.whereFrom || "",
        remarks: formData.remarks || "",
      },
      validationSchema: validationSchema,
      onSubmit: async (data) => {
        setLoadIndicators(true);
        data.leadId = leadId;
        if (assessmentAvailable) {
          try {
            const response = await api.put(
              `/updateLeadDoAssessment/${assessmentId}`,
              data,
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
            if (response.status === 200) {
              toast.success(response.data.message);
              const assesmentId = assessmentId;
              setFormData((prv) => ({
                ...prv,
                ...data,
                assesmentId,
              }));
              handleNext();
            } else {
              toast.error(response.data.message);
            }
          } catch (error) {
            toast.error(error);
          } finally {
            setLoadIndicators(false);
          }
        } else {
          try {
            const response = await api.post("/createLeadDoAssessment", data, {
              headers: {
                "Content-Type": "application/json",
              },
            });
            if (response.status === 201) {
              toast.success(response.data.message);
              const assesmentId = response.data.assessmentId;
              setFormData((prv) => ({
                ...prv,
                ...data,
                assesmentId,
              }));
              handleNext();
            } else {
              toast.error(response.data.message);
            }
          } catch (error) {
            toast.error(error);
          } finally {
            setLoadIndicators(false);
          }
        }
      },
    });

    const getArrangeAssesmentData = async () => {
      try {
        const response = await api.get(
          `/getAllLeadInfoWithReferrerById/${leadId}`
        );
        setArrangeassesmentData(response.data);
        setCenterId(response.data.centerId);
        formik.setFieldValue("centerId", response.data.centerId);
        const timeSlotOffered = response?.data?.assessmentArrange[0]?.time;
        formik.setFieldValue("timeSlotOffered", timeSlotOffered);
      } catch (error) {
        toast.error("Error Fetch Data ", error);
      }
    };

    const fetchData = async () => {
      try {
        const studentData = await fetchAllStudentsWithIds();
        setStudentData(studentData);
      } catch (error) {
        toast.error(error);
      }
    };

    const fetchCourses = async (centerId) => {
      try {
        if (centerId) {
          const course = await fetchAllCoursesWithIdsC(centerId);
          const formattedCourses = course.map((course) => ({
            value: course.id,
            label: course.courseNames || course.courseName,
          }));
          setCourseOptions(formattedCourses);
          // Check if the existing courseId is valid
          if (
            formik.values.courseId &&
            !formattedCourses.find((c) => c.value === formik.values.courseId)
          ) {
            formik.setFieldValue("courseId", "");
            formik.setFieldValue("classId", "");
            setClassOptions([]);
          }
        } else {
          setCourseOptions([]);
          setClassOptions([]);
          formik.setFieldValue("courseId", "");
          formik.setFieldValue("classId", "");
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast.error(error.message || "Failed to fetch courses");
        setCourseOptions([]);
        setClassOptions([]);
      }
    };

    const fetchClasses = async (courseId) => {
      try {
        if (courseId) {
          const classes = await fetchAllClassesWithIdsC(courseId);
          const formattedClasses = classes.map((classListing) => ({
            value: classListing.id,
            label: classListing.classNames,
          }));
          setClassOptions(formattedClasses);
          // Check if the existing classId is valid
          if (
            formik.values.classId &&
            !formattedClasses.find((c) => c.value === formik.values.classId)
          ) {
            formik.setFieldValue("classId", "");
          }
        } else {
          setClassOptions([]);
          formik.setFieldValue("classId", "");
        }
      } catch (error) {
        console.error("Error fetching classes:", error);
        toast.error(error.message || "Failed to fetch classes");
        setClassOptions([]);
      }
    };

    useEffect(() => {
      const getData = async () => {
        try {
          const response = await api.get(
            `/getLeadAssessmentDataByLeadId/${leadId}`
          );
          if (response?.data?.leadDoAssessmentModel?.length > 0) {
            setAssessmentAvailable(true);
            setAssessmentId(response.data.leadDoAssessmentModel[0].id);
            const year =
              response.data.leadDoAssessmentModel[0].year &&
              response.data.leadDoAssessmentModel[0].year.substring(0, 10);
            formik.setValues({
              ...response.data.leadDoAssessmentModel[0],
              year: year,
              assessmentDate: currentDate,
            });
            // Fetch courses and classes for the existing centerId
            if (response.data.leadDoAssessmentModel[0].centerId) {
              setCenterId(response.data.leadDoAssessmentModel[0].centerId);
              await fetchCourses(response.data.leadDoAssessmentModel[0].centerId);
              if (response.data.leadDoAssessmentModel[0].courseId) {
                await fetchClasses(response.data.leadDoAssessmentModel[0].courseId);
              }
            }
          } else {
            const leadResponse = await api.get(
              `/getAllLeadInfoWithReferrerById/${leadId}`
            );
            const dob =
              leadResponse.data.dateOfBirth &&
              leadResponse.data.dateOfBirth.substring(0, 10);
            // Check if courseId and classId are present in the API response
            const hasCourseAndClass =
              leadResponse.data.courseId && leadResponse.data.classId;
            formik.setValues({
              year: dob,
              name: leadResponse.data.studentName,
              assessmentDate: currentDate,
              remarks: leadResponse.data.remark,
              referredBy: leadResponse.data.referBy,
              whereFrom: leadResponse.data.marketingSource,
              levelAssessed: "",
              centerId: leadResponse.data.centerId,
              courseId: hasCourseAndClass
                ? String(leadResponse.data.courseId)
                : "",
              classId: hasCourseAndClass ? String(leadResponse.data.classId) : "",
            });
            // Set centerId and fetch courses/classes
            if (leadResponse.data.centerId) {
              setCenterId(leadResponse.data.centerId);
              await fetchCourses(leadResponse.data.centerId);
              // Only fetch classes if courseId is present
              if (hasCourseAndClass && leadResponse.data.courseId) {
                await fetchClasses(leadResponse.data.courseId);
              }
            }
          }
        } catch (error) {
          toast.error("Error fetching lead data", error);
        }
      };
      getData();
      getArrangeAssesmentData();
      fetchData();
    }, []);

    const calculateAge = (dob) => {
      if (!dob) return "0 years, 0 months";
      const birthDate = new Date(dob);
      const today = new Date();
      if (isNaN(birthDate.getTime())) return "0 years, 0 months";
      let years = today.getFullYear() - birthDate.getFullYear();
      let months = today.getMonth() - birthDate.getMonth();
      if (months < 0) {
        years--;
        months += 12;
      }
      return `${years} years, ${months} months`;
    };

    useEffect(() => {
      if (formik.values.year) {
        formik.setFieldValue("age", calculateAge(formik.values.year));
      }
    }, [formik.values.year]);

    useEffect(() => {
      if (centerId) {
        fetchCourses(centerId);
      }
    }, [centerId]);

    useEffect(() => {
      if (formik.values.courseId) {
        fetchClasses(formik.values.courseId);
      } else {
        setClassOptions([]);
        formik.setFieldValue("classId", "");
      }
    }, [formik.values.courseId]);

    useImperativeHandle(ref, () => ({
      AssessmentChild: formik.handleSubmit,
    }));

    return (
      <section>
        <form
          onSubmit={formik.handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !formik.isSubmitting) {
              e.preventDefault();
            }
          }}
        >
          <div className="mt-3">
            <h5 className="headColor mb-4">Child Particulars</h5>
            <div className="container-fluid row">
              <div className="col-md-6 col-12 mb-4">
                <label>
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  readOnly
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="error text-danger ">
                    <small>{formik.errors.name}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label>
                  Assessment Date <span className="text-danger">*</span>
                </label>
                <CustomDatePicker
                  name="assessmentDate"
                  value={formik.values.assessmentDate}
                  onChange={(date) =>
                    formik.setFieldValue("assessmentDate", date)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.errors.assessmentDate}
                  touched={formik.touched.assessmentDate}
                  showPopperArrow={true}
                />
                {formik.touched.assessmentDate &&
                  formik.errors.assessmentDate && (
                    <div className="error text-danger">
                      {formik.errors.assessmentDate}
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label>Age</label>
                <input
                  type="text"
                  name="age"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.age}
                  readOnly
                />
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label>Date Of Birth</label>
                <br />
                <CustomDatePicker
                  name="year"
                  value={formik.values.year}
                  onChange={(date) => formik.setFieldValue("year", date)}
                  onBlur={formik.handleBlur}
                  error={formik.errors.year}
                  touched={formik.touched.year}
                  showPopperArrow={true}
                />
                {formik.touched.year && formik.errors.year && (
                  <div className="error text-danger">{formik.errors.year}</div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <p>
                  Picture Taken{" "}
                  <span className="text-sm">
                    (To Send To Prospective Parents)
                  </span>
                </p>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="pictureToken"
                    id="inlineRadio1"
                    value="yes"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    checked={formik.values.pictureToken === "yes"}
                  />
                  <label className="form-check-label" htmlFor="inlineRadio1">
                    Yes
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="pictureToken"
                    id="inlineRadio2"
                    value="no"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    checked={formik.values.pictureToken === "no"}
                  />
                  <label className="form-check-label" htmlFor="inlineRadio2">
                    No
                  </label>
                </div>
                {formik.touched.pictureToken && formik.errors.pictureToken ? (
                  <div className="text-danger">
                    {formik.errors.pictureToken}
                  </div>
                ) : null}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <p>Payment Mode</p>
                <select
                  className="form-select"
                  name="paymentMode"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.paymentMode}
                >
                  <option selected></option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Cash">Cash</option>
                  <option value="Paynow">PayNow</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
                {formik.touched.paymentMode && formik.errors.paymentMode ? (
                  <div className="text-danger">{formik.errors.paymentMode}</div>
                ) : null}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label>Time Slot Offered</label>
                <div className="col-12 d-flex ">
                  <input
                    type="time"
                    name="timeSlotOffered"
                    className="form-control"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.timeSlotOffered}
                  />
                </div>
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label>Referred By (Student Name)</label>
                <input
                  {...formik.getFieldProps("referredBy")}
                  className={`form-control ${
                    formik.touched.referredBy && formik.errors.referredBy
                      ? "is-invalid"
                      : ""
                  }`}
                  readOnly
                />
              </div>
              <div className="col-md-6 col-12 mb-4">
                <p>T-Shirt Size</p>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="tshirtSize"
                    id="tshirtSize"
                    value="Taken"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    checked={formik.values.tshirtSize === "Taken"}
                  />
                  <label className="form-check-label" htmlFor="tshirtSize">
                    Taken
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="tshirtSize"
                    id="tshirtSize"
                    value="Not Taken"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    checked={formik.values.tshirtSize === "Not Taken"}
                  />
                  <label className="form-check-label" htmlFor="tshirtSize">
                    Not Taken
                  </label>
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <div>
                    <label
                      for="exampleFormControlInput1"
                      className="form-label"
                    >
                      Level Assessed
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="levelAssessed"
                      id="ARTY_BELIVER"
                      value="ARTY_BELIVER"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      checked={formik.values.levelAssessed === "ARTY_BELIVER"}
                    />
                    <label className="form-check-label" htmlFor="ARTY_BELIVER">
                      Arty Beliver
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="levelAssessed"
                      id="ARTY_DREAMER"
                      value="ARTY_DREAMER"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      checked={formik.values.levelAssessed === "ARTY_DREAMER"}
                    />
                    <label className="form-check-label" htmlFor="ARTY_DREAMER">
                      Arty Dreamer
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="levelAssessed"
                      id="ARTY_PURSUER"
                      value="ARTY_PURSUER"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      checked={formik.values.levelAssessed === "ARTY_PURSUER"}
                    />
                    <label className="form-check-label" htmlFor="ARTY_PURSUER">
                      Arty Pursuer
                    </label>
                  </div>
                  {formik.touched.levelAssessed &&
                    formik.touched.levelAssessed && (
                      <div className="text-danger">
                        {formik.errors.levelAssessed}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label>Sibling(s)</label>
                <input
                  type="text"
                  name="sibling"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.sibling}
                />
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label>Where Did You Here From ?</label>
                <input
                  type="text"
                  name="whereFrom"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.whereFrom}
                />
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label className="form-label">
                  Course<span className="text-danger">*</span>
                </label>
                <Select
                  options={courseOptions}
                  name="courseId"
                  data-name="courseId"
                  value={courseOptions.find(
                    (option) => option.value === formik.values.courseId
                  )}
                  onChange={(selectedOption) => {
                    formik.setFieldValue(
                      "courseId",
                      selectedOption ? selectedOption.value : ""
                    );
                    formik.setFieldValue("classId", "");
                  }}
                  onBlur={() => formik.setFieldTouched("courseId", true)}
                  placeholder="Select Course"
                  isSearchable
                  isClearable
                  isDisabled={!centerId}
                  className={
                    formik.touched.courseId && formik.errors.courseId
                      ? "is-invalid"
                      : ""
                  }
                />
                {formik.touched.courseId && formik.errors.courseId && (
                  <div className="invalid-feedback">
                    {formik.errors.courseId}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label className="form-label">
                  Class Listing<span className="text-danger">*</span>
                </label>
                <Select
                  options={classOptions}
                  name="classId"
                  data-name="classId"
                  value={classOptions.find(
                    (option) => option.value === formik.values.classId
                  )}
                  onChange={(selectedOption) =>
                    formik.setFieldValue(
                      "classId",
                      selectedOption ? selectedOption.value : ""
                    )
                  }
                  onBlur={() => formik.setFieldTouched("classId", true)}
                  placeholder="Select Class"
                  isSearchable
                  isClearable
                  isDisabled={!formik.values.courseId}
                  className={
                    formik.touched.classId && formik.errors.classId
                      ? "is-invalid"
                      : ""
                  }
                />
                {formik.touched.classId && formik.errors.classId && (
                  <div className="invalid-feedback">
                    {formik.errors.classId}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label for="floatingTextarea2">Remark</label>
                <div className="">
                  <textarea
                    type="text"
                    name="remarks"
                    className="form-control"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.remarks}
                    id="floatingTextarea2"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </form>
      </section>
    );
  }
);

export default AssessmentChild;